// src/context/ThemeContext.jsx
import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";

/**
 * ThemeContext: tarjoaa teeman ("light" | "dark") ja vaihtofunktion.
 * - Tallennetaan teema localStorageen ('theme')
 * - Synkataan <html>-elementin 'dark' -luokka Tailwindia varten
 * - Synkataan myös data-theme, color-scheme ja CSS-muuttujat
 * - Oletus: dark
 */

const ThemeContext = createContext(null);

function getInitialTheme() {
  try {
    const saved = localStorage.getItem("theme");
    if (saved === "dark" || saved === "light") return saved;
  } catch {}
  return "dark";
}

function applyTheme(theme) {
  const root = document.documentElement;
  const isDark = theme === "dark";

  root.classList.toggle("dark", isDark);
  root.dataset.theme = isDark ? "dark" : "light";
  root.style.colorScheme = isDark ? "dark" : "light";

  if (isDark) {
    root.style.setProperty("--bg-start", "#0A0014");
    root.style.setProperty("--bg-end", "#2A006B");
  } else {
    root.style.setProperty("--bg-start", "#99925c");
    root.style.setProperty("--bg-end", "#fff455");
  }
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  // Käytetään layout-efektiä, jotta teema ehtii DOMiin ennen maalausta
  useLayoutEffect(() => {
    applyTheme(theme);

    try {
      localStorage.setItem("theme", theme);
    } catch {}
  }, [theme]);

  const toggleTheme = () => setTheme((t) => (t === "dark" ? "light" : "dark"));

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error("useTheme pitää kutsua ThemeProviderin sisällä.");
  }
  return ctx;
}